import { buildBusinessClaimsVendorGroups } from './business-claims-vendor-groups';
import { transactionService } from './transaction-service';
import type {
  ApplyBusinessClaimsTaxInput,
  ApplyBusinessClaimsTaxResult,
  BusinessClaimsVendorGroup,
  ScanBusinessClaimsInput,
  VendorBusinessClaimApplyRow,
} from '../types/business-claims.types';
import type { ServiceResponse, Transaction } from '../types/transaction.types';
import { isExpenseOutflow } from './reporting/transaction-amount-utils';

async function fetchAllDebitsInScope(input: ScanBusinessClaimsInput): Promise<ServiceResponse<Transaction[]>> {
  const { userId, dateFrom, dateTo, accountIds, categoryIds } = input;

  if (!categoryIds.length) {
    return { data: [] };
  }

  const all: Transaction[] = [];
  let page = 1;
  const limit = 1000;

  while (true) {
    const result = await transactionService.listTransactions({
      userId,
      dateFrom,
      dateTo,
      accountIds: accountIds?.length ? accountIds : undefined,
      categoryIds,
      type: 'debit',
      page,
      limit,
      sortOrder: 'asc',
    });

    if (result.error) {
      return { error: result.error };
    }

    const batch = result.data?.transactions ?? [];
    all.push(...batch.filter(isExpenseOutflow));

    const total = result.data?.total ?? 0;
    if (all.length >= total || batch.length < limit) {
      break;
    }
    page += 1;
  }

  return { data: all };
}

class BusinessClaimsService {
  async scanVendorGroups(input: ScanBusinessClaimsInput): Promise<ServiceResponse<BusinessClaimsVendorGroup[]>> {
    const fetchResult = await fetchAllDebitsInScope(input);
    if (fetchResult.error) {
      return { error: fetchResult.error };
    }
    return { data: buildBusinessClaimsVendorGroups(fetchResult.data ?? []) };
  }

  async applyTaxFields(input: ApplyBusinessClaimsTaxInput): Promise<ServiceResponse<ApplyBusinessClaimsTaxResult>> {
    const { userId, transactionIds, businessUsePercent, gstStatus, receiptAttached } = input;
    return transactionService.bulkUpdateTransactionTaxFields(transactionIds, userId, {
      businessUsePercent,
      gstStatus,
      receiptAttached,
    });
  }

  async applyVendorBusinessUse(
    userId: string,
    rows: VendorBusinessClaimApplyRow[],
    options: { overwriteExisting: boolean }
  ): Promise<ServiceResponse<ApplyBusinessClaimsTaxResult>> {
    let updated = 0;
    let failed = 0;
    let skipped = 0;
    const errors: string[] = [];

    for (const row of rows) {
      if (!row.transactionIds.length || row.businessUsePercent === undefined) continue;
      const result = await transactionService.bulkUpdateTransactionTaxFields(
        row.transactionIds,
        userId,
        { businessUsePercent: row.businessUsePercent },
        { onlyUnsetBusinessUsePercent: !options.overwriteExisting }
      );
      if (result.error) {
        failed += row.transactionIds.length;
        errors.push(result.error.message);
        continue;
      }
      updated += result.data?.updated ?? 0;
      failed += result.data?.failed ?? 0;
      skipped += result.data?.skipped ?? 0;
      errors.push(...(result.data?.errors ?? []));
    }

    return { data: { updated, failed, skipped, errors } };
  }
}

export const businessClaimsService = new BusinessClaimsService();
